import styled from 'styled-components'
import { theme } from '../theme/theme'
import { BodySmall, Title4 } from './styles/Typography'
import { DropZone, LoadingState } from './inputs/DropZone'
import { TextInput } from './inputs/Text'
import { SelectInput, SelectOption } from './inputs/Select'
import { OnClickButton } from './buttons/onclick/OnClickButton'
import { useCallback, useEffect, useState } from 'react'
import {
  UserDataMessage,
  SupportedLanguage,
  isSupportedLanguage,
  SupportedMarket,
  isSupportedMarket,
} from '@estercare/ester-shared'
import { UseMutationResult } from '@tanstack/react-query'
import { CreatedUser, CreateUsersResponse } from '../api/ester-b2b-api/private/users/types'
import { BodySmallMedium } from './styles/Typography'
import { validateSingleUserForm, hasValidationErrors, SingleUserFormDataType } from '../utils/validation'
import { useTranslation } from 'react-i18next'
import { getLanguageOptions } from '../utils/languageOptions'
import { Modal } from './modals/Modal'

type Props = {
  onClose: () => void
  onFileUploadSuccess: () => void
  onSingleUserSuccess: () => void
  postUserMutation: UseMutationResult<
    CreateUsersResponse,
    Error,
    {
      usersFileData: FormData
    }
  >
  postSingleUserMutation: UseMutationResult<
    CreatedUser[],
    Error,
    {
      users: SingleUserFormDataType[]
    }
  >
}

enum FileUploadState {
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

enum ViewState {
  SINGLE_USER_FORM = 'SINGLE_USER_FORM',
  CSV_UPLOAD = 'CSV_UPLOAD',
}

const getErrorMessages = (t: (key: string) => string): Record<UserDataMessage, string> => ({
  [UserDataMessage.TOO_SHORT]: t('common.file.needsTwoRows'),
  [UserDataMessage.MISSING_FIELDS]: t('common.file.needsThreeColumns'),
  [UserDataMessage.MISSING_FIELD]: t('common.file.missingValues'),
  [UserDataMessage.INVALID_EMAIL]: t('common.file.invalidEmails'),
  [UserDataMessage.VALID]: t('common.file.fileValid'),
  [UserDataMessage.MANAGER_NOT_A_BOOLEAN]: t('common.file.managerColumnFormat'),
  [UserDataMessage.INVALID_LANGUAGE]: t('common.file.invalidLanguage'),
  [UserDataMessage.INVALID_MARKET]: t('common.file.invalidMarket'),
})

const getManagerOptions = (t: (key: string) => string): SelectOption[] => [
  { value: 'false', label: t('common.form.no') },
  { value: 'true', label: t('common.form.yes') },
]

const getMarketOptions = (t: (key: string) => string): SelectOption[] => [
  { value: SupportedMarket.SWEDEN, label: t('common.market.sweden') },
  { value: SupportedMarket.NORWAY, label: t('common.market.norway') },
]

export const AddEmployeeModal = ({
  onClose,
  onFileUploadSuccess,
  onSingleUserSuccess,
  postUserMutation,
  postSingleUserMutation,
}: Props) => {
  const { t } = useTranslation()
  const errorMessages = getErrorMessages(t)
  const managerOptions = getManagerOptions(t)
  const [currentView, setCurrentView] = useState<ViewState>(ViewState.SINGLE_USER_FORM)
  const [fileUploadState, setFileUploadState] = useState<LoadingState>(null)
  const [errorMessage, setErrorMessage] = useState<string>()

  const [formData, setFormData] = useState<SingleUserFormDataType>({
    firstName: '',
    lastName: '',
    email: '',
    isManager: false,
    language: SupportedLanguage.SWEDISH,
    department: '',
    market: SupportedMarket.SWEDEN,
  })

  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  const languageOptions = getLanguageOptions(t)
  const marketOptions = getMarketOptions(t)

  const isCSVUpload = currentView === ViewState.CSV_UPLOAD
  const activeMutation = isCSVUpload ? postUserMutation : postSingleUserMutation
  const { isSuccess, isError, isPending, error } = activeMutation

  const uploadFile = useCallback(
    (files: File[]) => {
      if (files.length > 0) {
        const usersFileData = new FormData()
        files.forEach((file) => usersFileData.append('file', file))
        postUserMutation.mutate({ usersFileData })
      }
    },
    [postUserMutation]
  )

  const handleSingleUserSubmit = () => {
    setFormErrors({})
    const errors = validateSingleUserForm(formData, t)

    if (hasValidationErrors(errors)) {
      setFormErrors(errors)
      return
    }

    setErrorMessage(undefined)
    const usersToSubmit = [{ ...formData, department: formData.department?.trim() || undefined }]
    postSingleUserMutation.mutate({ users: usersToSubmit })
  }

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      isManager: false,
      language: SupportedLanguage.SWEDISH,
      department: '',
      market: SupportedMarket.SWEDEN,
    })
    setFormErrors({})
  }

  const handleSuccess = useCallback(() => {
    if (currentView === ViewState.SINGLE_USER_FORM) {
      onSingleUserSuccess()
      resetForm()
      setErrorMessage(undefined)
    } else {
      setFileUploadState(FileUploadState.SUCCESS)
      onFileUploadSuccess()
      setErrorMessage(undefined)
    }
    setFileUploadState(null)
  }, [currentView, onSingleUserSuccess, onFileUploadSuccess])

  const handleError = useCallback(() => {
    if (!error) return
    if (isCSVUpload) {
      setFileUploadState(FileUploadState.FAILED)
      const match = Object.values(UserDataMessage).find((key) => error.message.includes(key))
      setErrorMessage(match ? errorMessages[match] : t('common.errors.tryAgainOrContact'))
      return
    }

    const message = (error.message || '').toLowerCase()
    if (message.includes('already exists')) {
      setErrorMessage(t('admin.employees.employeesAlreadyExistingNoCount'))
      return
    }
    setErrorMessage(t('common.errors.tryAgainOrContact'))
  }, [error, isCSVUpload, errorMessages, t])

  useEffect(() => {
    if (isPending && isCSVUpload) {
      setFileUploadState(FileUploadState.LOADING)
    }
  }, [isPending, isCSVUpload])

  useEffect(() => {
    if (isSuccess) {
      handleSuccess()
    }
  }, [isSuccess, handleSuccess])

  useEffect(() => {
    if (isError) {
      handleError()
    }
  }, [isError, handleError])

  const handleViewSwitch = (view: ViewState) => {
    setCurrentView(view)
    setFileUploadState(null)
    setErrorMessage(undefined)
    if (view === ViewState.SINGLE_USER_FORM) {
      resetForm()
    }
  }

  return (
    <Modal
      onClose={() => {
        setFileUploadState(null)
        resetForm()
        onClose()
      }}
      backgroundColor={theme.color.beigeLight}
    >
      <WideModalContent>
        {currentView === ViewState.SINGLE_USER_FORM ? (
          <>
            <ModalTitle>{t('common.addEmployee.title')}</ModalTitle>

            {errorMessage && currentView === ViewState.SINGLE_USER_FORM && <ErrorMessage>{errorMessage}</ErrorMessage>}

            <FormContainer>
              <TextInput
                label={t('common.form.firstName')}
                placeholder={t('common.form.firstNamePlaceholder')}
                value={formData.firstName}
                onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                error={formErrors.firstName}
              />

              <TextInput
                label={t('common.form.lastName')}
                placeholder={t('common.form.lastNamePlaceholder')}
                value={formData.lastName}
                onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                error={formErrors.lastName}
              />

              <TextInput
                label={t('common.form.email')}
                placeholder={t('common.form.emailPlaceholder')}
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                error={formErrors.email}
              />

              <TextInput
                label={t('common.form.department')}
                placeholder={t('common.form.departmentPlaceholder')}
                value={formData.department || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, department: e.target.value }))}
                error={formErrors.department}
              />
              <SelectInput
                label={t('common.form.language')}
                options={languageOptions}
                value={formData.language || SupportedLanguage.SWEDISH}
                onChange={(value: string) => {
                  if (isSupportedLanguage(value)) {
                    setFormData((prev) => ({ ...prev, language: value }))
                  }
                }}
                placeholder={t('common.form.choose')}
              />
              <SelectInput
                label={t('common.form.market')}
                options={marketOptions}
                value={formData.market}
                onChange={(value: string) => {
                  if (isSupportedMarket(value)) {
                    setFormData((prev) => ({ ...prev, market: value }))
                  }
                }}
                placeholder={t('common.form.choose')}
              />
              <SelectInput
                label={t('common.addEmployee.assignManagerRights')}
                options={managerOptions}
                value={String(formData.isManager)}
                onChange={(value) => setFormData((prev) => ({ ...prev, isManager: value === 'true' }))}
                placeholder={t('common.form.choose')}
              />
            </FormContainer>

            <OnClickButton
              text={t('common.form.add')}
              loading={isPending}
              onClick={handleSingleUserSubmit}
              buttonStyle={{ width: '100%' }}
            />

            <ClickableText onClick={() => handleViewSwitch(ViewState.CSV_UPLOAD)}>
              {t('common.addEmployee.wantToAddMany')}
            </ClickableText>
          </>
        ) : (
          <>
            <ModalTitle>{t('common.addEmployee.addMultipleTitle')}</ModalTitle>
            <BodySmall>{t('common.addEmployee.addMultipleDescription')}</BodySmall>
            <StyledBodySmall>{t('common.addEmployee.templateInstruction')}</StyledBodySmall>
            <DropZone
              acceptedFiles={{ 'text/csv': ['.csv'] }}
              onDrop={(files) => uploadFile(files)}
              fileUploadState={fileUploadState}
              errorMessage={errorMessage}
            />

            <ClickableText onClick={() => handleViewSwitch(ViewState.SINGLE_USER_FORM)}>
              {t('common.addEmployee.wantToAddOne')}
            </ClickableText>
          </>
        )}
      </WideModalContent>
    </Modal>
  )
}

const ModalTitle = styled(Title4)`
  margin-bottom: ${theme.spacing.medium}px;
`

const StyledBodySmall = styled(BodySmall)`
  margin-bottom: ${theme.spacing.mediumLarge}px;
`

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${theme.spacing.mediumLarge}px;
  row-gap: ${theme.spacing.medium}px;
  margin-bottom: ${theme.spacing.mediumLarge}px;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    grid-template-columns: 1fr;
    row-gap: ${theme.spacing.medium}px;
  }

  @media screen and (max-width: ${theme.breakpoint.small}px) {
    row-gap: ${theme.spacing.small}px;
  }
`

const WideModalContent = styled.div`
  width: 660px;
  @media screen and (max-width: ${theme.breakpoint.small}px) {
    margin: -${theme.spacing.small}px;
    padding: ${theme.spacing.large}px;
    width: calc(100vw - 32px);
  }
`

const ClickableText = styled(BodySmallMedium)`
  margin-top: ${theme.spacing.medium}px;
  text-align: center;
  cursor: pointer;
  color: ${theme.color.plumMid};
  &:hover {
    text-decoration: underline;
  }
`

const ErrorMessage = styled(BodySmall)`
  color: ${theme.color.red};
  margin-bottom: ${theme.spacing.medium}px;
  text-align: center;
`
